'use client'

import { useMemo, useState } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { compact, isError } from 'lodash'
import { SearchIcon } from 'lucide-react'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import { FilterScrollFade } from '~/app/accounting/filters/FilterScrollFade'
import {
  buildTokenFilterKey,
  COLUMN_IDS,
  getColumnFilterValue,
  parseTokenFilterKey,
  setColumnFilterValue,
} from '~/app/accounting/filters/helpers'
import {
  getFilteredOrgBalances,
  getTokensSortedByValue,
} from '~/app/accounting/useAccountingTokenPositions'
import { parseMultiSelectFilterValue } from '~/components/Table'
import {
  CheckboxGroupInput,
  EmptyState,
  Group,
  LoadingSpinner,
  TokenDisplayView,
} from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg, useActiveOrgSmartAccountsChainIds } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'
import { getChainLabel } from '~/utils/chainLabels'
import {
  getVaultAssetSymbol,
  getVaultProviderName,
  isLegacyVault,
  isVaultToken,
} from '~/utils/vault'

type TokenFilterRow = {
  key: string
  address: string
  chainId: SupportedChainId
  symbol: string
  logoUri: string | undefined
  isPosition: boolean
}

export const TokenFilterButton = ({
  columnFilters,
  setColumnFilters,
}: {
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) => {
  const { org } = useActiveOrg()
  // useOrgBalances defaults showSpamTokens to false, so the server already
  // excludes classified spam and org-hidden tokens from this list.
  const { balances: realtimeOrgBalances, isLoading: isBalancesLoading } =
    useOrgBalances(org?.id)
  const activeChainIds = useActiveOrgSmartAccountsChainIds()

  const rawValue = getColumnFilterValue(columnFilters, COLUMN_IDS.TOKEN)
  const appliedKeys = useMemo(() => {
    if (!rawValue) return [] as string[]
    const parsed = parseMultiSelectFilterValue(rawValue)
    return isError(parsed) ? ([] as string[]) : parsed
  }, [rawValue])

  // Currently held tokens across the org, largest holdings first.
  const heldTokens = useMemo(
    () =>
      getTokensSortedByValue(
        getFilteredOrgBalances({
          realtimeOrgBalances,
          allowedAddresses: null,
          chainIds: undefined,
        }),
      ),
    [realtimeOrgBalances],
  )

  // Applied tokens that are no longer held (sold positions, hand-edited URLs)
  // still get a row so they can be unchecked.
  const allTokens = useMemo(() => {
    const held = heldTokens
      // Skip tokens on networks the org has turned off (they'd render locked).
      .filter((token) => activeChainIds.includes(token.chainId))
      .map((token) => ({
        key: buildTokenFilterKey({
          chainId: token.chainId,
          address: token.address,
        }),
        address: token.address as string,
        chainId: token.chainId,
        fallbackSymbol: token.symbol,
      }))
    const heldKeys = new Set(held.map((token) => token.key))
    const missingApplied = compact(
      appliedKeys
        .filter((key) => !heldKeys.has(key))
        .map((key) => {
          const parsed = parseTokenFilterKey(key)
          if (!parsed) return null
          return {
            key,
            address: parsed.address,
            chainId: parsed.chainId as SupportedChainId,
            fallbackSymbol: '',
          }
        }),
    )
    return [...held, ...missingApplied]
  }, [heldTokens, appliedKeys, activeChainIds])

  // One batched metadata fetch for the list; results align by index.
  const metadataItems = useMultipleTokensMetadata(
    allTokens.map((token) => ({
      address: token.address as IAddress,
      chainId: token.chainId,
    })),
  )

  const rows: TokenFilterRow[] = allTokens.map((token, index) => {
    const metadata = metadataItems[index]
    const isPosition = isVaultToken(token.chainId, token.address as IAddress)
    const vaultAssetSymbol = isPosition
      ? getVaultAssetSymbol(token.chainId, token.address as IAddress)
      : undefined
    // The metadata loading placeholder symbol is the shortened address —
    // prefer the balance-provided symbol until validToken.
    return {
      key: token.key,
      address: token.address,
      chainId: token.chainId,
      symbol:
        vaultAssetSymbol ||
        (metadata?.validToken
          ? metadata.symbol
          : token.fallbackSymbol || metadata?.symbol || 'UNKNOWN'),
      logoUri: metadata?.logoUri,
      isPosition,
    }
  })

  const [localKeys, setLocalKeys] = useState<string[]>(appliedKeys)
  const [search, setSearch] = useState('')
  // Sync pending selections when the applied value changes externally (URL
  // edit, or the tokens tab "Filter transactions by token" action).
  const [syncedRawValue, setSyncedRawValue] = useState(rawValue)
  if (syncedRawValue !== rawValue) {
    setSyncedRawValue(rawValue)
    setLocalKeys(appliedKeys)
  }

  const isLocalEmpty = localKeys.length === 0
  const isAppliedEmpty = appliedKeys.length === 0
  const hasChanged =
    localKeys.length !== appliedKeys.length ||
    localKeys.some((key) => !appliedKeys.includes(key))

  const clearFilter = () => {
    setColumnFilters((prev) => setColumnFilterValue(prev, COLUMN_IDS.TOKEN, ''))
  }

  const activeLabel =
    appliedKeys.length === 1
      ? (rows.find((row) => row.key === appliedKeys[0])?.symbol ?? 'Token')
      : `Tokens (${appliedKeys.length})`

  return (
    <FilterPopoverButton
      label="Token"
      activeLabel={activeLabel}
      isAppliedEmpty={isAppliedEmpty}
      isLocalEmpty={isLocalEmpty}
      hasChanged={hasChanged}
      onApply={() => {
        setColumnFilters((prev) =>
          setColumnFilterValue(
            prev,
            COLUMN_IDS.TOKEN,
            localKeys.length > 0 ? JSON.stringify(localKeys) : '',
          ),
        )
        // Apply/Reset close without going through onClose — clear here too.
        setSearch('')
      }}
      onReset={() => {
        setLocalKeys([])
        setSearch('')
        clearFilter()
      }}
      onClear={() => {
        setLocalKeys([])
        setSearch('')
        clearFilter()
      }}
      onClose={() => {
        setLocalKeys(appliedKeys)
        setSearch('')
      }}
    >
      <div className="flex flex-col gap-3">
        <Input
          inputSize="small"
          placeholder="Search by token or address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchIcon className="h-3.5 w-3.5 text-secondary" />}
          onClearInput={search ? () => setSearch('') : undefined}
        />
        <TokenOptionsList
          rows={rows}
          isLoading={isBalancesLoading}
          search={search}
          localKeys={localKeys}
          onLocalKeysChange={setLocalKeys}
          activeChainIds={activeChainIds}
        />
      </div>
    </FilterPopoverButton>
  )
}

// Mounts with the popover, so the order snapshot freezes at open — the 10s
// balance refetch can't resort the list under the cursor.
const TokenOptionsList = ({
  rows,
  isLoading,
  search,
  localKeys,
  onLocalKeysChange,
  activeChainIds,
}: {
  rows: TokenFilterRow[]
  isLoading: boolean
  search: string
  localKeys: string[]
  onLocalKeysChange: (keys: string[]) => void
  activeChainIds: number[]
}) => {
  const [frozenOrder, setFrozenOrder] = useState(
    () => new Map(rows.map((row, index) => [row.key, index])),
  )
  // Balances can land after the popover opens — capture the order once.
  if (frozenOrder.size === 0 && rows.length > 0) {
    setFrozenOrder(new Map(rows.map((row, index) => [row.key, index])))
  }

  // Keys outside the snapshot sort last, keeping rows' largest-first order.
  const orderedRows = [...rows].sort(
    (a, b) =>
      (frozenOrder.get(a.key) ?? rows.length) -
      (frozenOrder.get(b.key) ?? rows.length),
  )

  const query = search.trim().toLowerCase()
  const visibleRows = query
    ? orderedRows.filter(
        (row) =>
          row.symbol.toLowerCase().includes(query) ||
          row.address.toLowerCase().includes(query),
      )
    : orderedRows

  if (visibleRows.length === 0) {
    return (
      // Keep the label in place so the popover doesn't jump.
      <Group label="Token">
        {isLoading && rows.length === 0 ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="md" thickness="normal" variant="default" />
          </div>
        ) : (
          <EmptyState className="py-4 text-center text-sm">
            No tokens found
          </EmptyState>
        )}
      </Group>
    )
  }

  return (
    <FilterScrollFade>
      <CheckboxGroupInput
        label="Token"
        size="small"
        value={localKeys}
        onChange={onLocalKeysChange}
        options={visibleRows.map((row) => ({
          value: row.key,
          display: () => (
            <div className="flex min-w-0 items-center gap-2">
              <TokenDisplayView
                address={row.address as IAddress}
                chainId={row.chainId}
                layout="standard"
                size="small"
                symbol={row.symbol}
                logoUri={row.logoUri}
                isPosition={row.isPosition}
                isInactiveChain={!activeChainIds.includes(row.chainId)}
                chainLabel={getChainLabel(row.chainId)}
                highlightText={search}
              />
              {isLegacyVault(row.address as IAddress) && (
                <span className="flex-shrink-0 text-xs text-secondary">
                  {getVaultProviderName(row.address as IAddress)}
                </span>
              )}
            </div>
          ),
        }))}
      />
    </FilterScrollFade>
  )
}
